import { create } from "zustand";
import type { ApiKeyInfo } from "@repo/travel-core";
import { createApiKey, listApiKeys, revokeApiKey, revealApiKey } from "@/lib/api";

interface ApiKeysState {
  keys: ApiKeyInfo[];
  loading: boolean;
  error: string | null;
  creating: boolean;
  revoking: number | null;
  fetchKeys: (getToken: () => Promise<string | null>) => Promise<void>;
  createKey: (
    getToken: () => Promise<string | null>,
    name?: string,
  ) => Promise<string | null>;
  revokeKey: (
    getToken: () => Promise<string | null>,
    id: number,
  ) => Promise<boolean>;
  copyKey: (
    getToken: () => Promise<string | null>,
    id: number,
  ) => Promise<string | null>;
  reset: () => void;
}

const initialState = {
  keys: [] as ApiKeyInfo[],
  loading: false,
  error: null as string | null,
  creating: false,
  revoking: null as number | null,
};

export const useApiKeysStore = create<ApiKeysState>((set) => ({
  ...initialState,

  fetchKeys: async (getToken) => {
    set({ loading: true, error: null });
    try {
      const data = await listApiKeys(getToken);
      set({ keys: data.keys, loading: false });
    } catch (err) {
      console.error("Error fetching API keys:", err);
      set({ error: "Failed to load API keys", loading: false });
    }
  },

  createKey: async (getToken, name) => {
    set({ creating: true });
    try {
      const result = await createApiKey(getToken, name);
      set((state) => ({
        keys: [
          ...state.keys,
          {
            id: result.id,
            prefix: result.prefix,
            suffix: result.suffix,
            copyable: true,
            createdAt: result.createdAt,
            ...(result.name && { name: result.name }),
          },
        ],
        creating: false,
      }));
      return result.key;
    } catch (err) {
      console.error("Error creating API key:", err);
      set({ error: "Failed to create API key", creating: false });
      return null;
    }
  },

  revokeKey: async (getToken, id) => {
    set({ revoking: id });
    try {
      await revokeApiKey(getToken, id);
      set((state) => ({
        keys: state.keys.filter((k) => k.id !== id),
        revoking: null,
      }));
      return true;
    } catch (err) {
      console.error("Error revoking API key:", err);
      set({ error: "Failed to revoke API key", revoking: null });
      return false;
    }
  },

  copyKey: async (getToken, id) => {
    try {
      const result = await revealApiKey(getToken, id);
      return result.key;
    } catch (err) {
      console.error("Error revealing API key:", err);
      return null;
    }
  },

  reset: () => set(initialState),
}));
